import React, { useMemo } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertTriangle } from 'lucide-react';
import { useEmbedTokenState } from '../lib/embedTokenBridge';

/**
 * EmbedBootstrapPage — the P0-hardened iframe landing route.
 *
 * The parent SDK loads the iframe at `/embed/v2/bootstrap?target=<...>`
 * without a JWT in the URL. This page waits for the token to arrive via
 * `postMessage` (through EmbedTokenProvider), then client-side navigates
 * to the actual embed page. `target` is a **safe** value like `insights`,
 * `kpi-monitor`, or `view/dashboard/dash_abc` — it carries no secret.
 *
 * If the parent never posts a token (e.g. sandboxed environment where
 * postMessage is blocked), the provider falls back to `?token=` and the
 * page renders a diagnostic panel.
 */

const TARGET_ALLOW_LIST = new Set([
  'insights',
  'kpi-monitor',
  'explore',
  'activity',
  'investigate',
  'marketplace',
  'er-diagram',
]);

function parseTarget(raw) {
  if (!raw) return null;
  // Simple resource: /embed/v2/{resource}
  if (TARGET_ALLOW_LIST.has(raw)) {
    return `/embed/v2/${raw}`;
  }
  // Content viewer: view/{resource}/{id}
  const m = /^view\/([a-z_-]+)\/([A-Za-z0-9_-]{1,64})$/.exec(raw);
  if (m) return `/embed/v2/view/${m[1]}/${encodeURIComponent(m[2])}`;
  return null;
}

export default function EmbedBootstrapPage() {
  const [params] = useSearchParams();
  const rawTarget = params.get('target') || '';
  const target = useMemo(() => parseTarget(rawTarget), [rawTarget]);
  const { token, source } = useEmbedTokenState();

  if (!target) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background" data-testid="embed-bootstrap-invalid">
        <div className="max-w-md rounded-lg border border-rose-500/40 bg-rose-500/5 p-5 text-center">
          <AlertTriangle className="w-6 h-6 text-rose-400 mx-auto mb-2" />
          <div className="text-sm font-semibold mb-1">Invalid embed target</div>
          <div className="text-xs text-muted-foreground break-all">
            {rawTarget ? `Unrecognized target: '${rawTarget}'` : 'Missing ?target= parameter'}
          </div>
          <div className="text-[10px] text-muted-foreground mt-3">
            Use one of: <code>insights</code>, <code>kpi-monitor</code>,
            or <code>view/&lt;resource&gt;/&lt;id&gt;</code>.
          </div>
        </div>
      </div>
    );
  }

  // Wait until we actually have a token — either from postMessage
  // bootstrap (v0.2 SDK) or from URL fallback (legacy). Preserve any
  // additional query params (e.g. `kind=` for Insights) so the target
  // page keeps its context.
  if (!token) {
    return (
      <div
        className="min-h-screen flex items-center justify-center gap-2 text-xs text-muted-foreground bg-background"
        data-testid="embed-bootstrap-waiting"
        data-source={source}
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Establishing secure session…
      </div>
    );
  }

  // Carry through any extra query params (kind, filters, etc.) but
  // NEVER carry the token into the URL — that's the whole point.
  const extra = new URLSearchParams(params);
  extra.delete('target');
  extra.delete('token');
  const search = extra.toString();
  const to = search ? `${target}?${search}` : target;

  return <Navigate to={to} replace data-testid="embed-bootstrap-redirect" />;
}
